import { weekOrderSP } from "@/constants/week-days";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import {
  ClasePoliticaEnum,
  RequestPolicy,
  RequestPolicyDetail,
} from "./request-policies.schema";
import {
  createRequestPolicy,
  CreateRequestPolicyProps,
  deleteRequestPolicies,
  DeleteRequestPoliciesProps,
  getRequestPolicies,
  updateRequestPolicy,
  UpdateRequestPolicyProps,
} from "@/services/request-policies.service";
import { Dispatch, SetStateAction } from "react";
import { AxiosError } from "axios";
import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import { NavigateFunction } from "react-router-dom";
import { formatCurrencyInputToNumber } from "@/utils/query-string";
import { handlePermissionError } from "@/utils/permission-check";

interface ResetCreationFormValorProps {
  setValue: UseFormSetValue<FieldValues>;
}

export const resetCreationFormValor = ({
  setValue,
}: ResetCreationFormValorProps) => {
  setValue("monto", "");
  setValue("dias", []);
  setValue("horaDesde", "");
  setValue("horaHasta", "");
};

interface ResetUpdateFormValorProps {
  setValue: UseFormSetValue<FieldValues>;
  policy?: RequestPolicyDetail | null;
}
export const resetUpdateFormValor = ({
  setValue,
  policy,
}: ResetUpdateFormValorProps) => {
  switch (policy?.clase) {
    case ClasePoliticaEnum.TopeDiario: {
      setValue("dias", []);
      setValue("horaDesde", "");
      setValue("horaHasta", "");
      setValue("monto", policy?.valor);
      break;
    }
    case ClasePoliticaEnum.RangoSemanal: {
      setValue("monto", "");
      setValue("horaDesde", "");
      setValue("horaHasta", "");
      setValue("dias", policy?.valor?.split(", "));
      break;
    }
    case ClasePoliticaEnum.Horario: {
      setValue("monto", "");
      setValue("dias", []);
      const horarios = policy?.valor?.split("-");
      setValue("horaDesde", horarios?.[0]);
      setValue("horaHasta", horarios?.[1]);
      break;
    }
    default:
      break;
  }
};
export const getValorByClase = (values: FieldValues) => {
  if (values?.clase?.[0] === ClasePoliticaEnum.TopeDiario) {
    return formatCurrencyInputToNumber(values.monto);
  }

  if (values?.clase?.[0] === ClasePoliticaEnum.RangoSemanal) {
    return values.dias
      .slice()
      .sort(
        (a: string, b: string) =>
          weekOrderSP.indexOf(a) - weekOrderSP.indexOf(b),
      )
      .join(", ");
  }

  if (values?.clase?.[0] === ClasePoliticaEnum.Horario) {
    return `${values?.horaDesde}-${values?.horaHasta}`;
  }

  return "";
};

export interface CreatePolicyHandlerProps {
  setLoadingCreatePolicy: Dispatch<SetStateAction<boolean>>;
  getValues: UseFormGetValues<FieldValues>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const handleCreatePolicyHandler = async ({
  setLoadingCreatePolicy,
  getValues,
  navigate,
  fetchPermissions,
}: CreatePolicyHandlerProps) => {
  try {
    setLoadingCreatePolicy(true);

    const values = getValues();

    const objToSend: CreateRequestPolicyProps = {
      clase: values?.clase,
      nombre: values?.nombre,
      tipoSolicitud: values?.tipoSolicitud as number[],
      valor: getValorByClase(values),
    };

    await createRequestPolicy(objToSend);

    toaster.create({
      type: "success",
      title: "Política creada con éxito.",
    });

    navigate(AppRoutes.REQUEST_POLICIES);

    setLoadingCreatePolicy(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string; errors?: string }>;

    const errorMsj = errorObj?.response?.data?.errors;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.REQUEST_POLICIES,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorMsj ??
          errorObj?.response?.data?.message ??
          "No se pudo agregar la política.",
      });
    }
    throw error;
  } finally {
    setLoadingCreatePolicy(false);
  }
};

export interface UpdatePolicyHandlerProps {
  setLoadingUpdatePolicy: Dispatch<SetStateAction<boolean>>;
  getValues: UseFormGetValues<FieldValues>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  id: number;
}

export const handleUpdatePolicyHandler = async ({
  getValues,
  navigate,
  setLoadingUpdatePolicy,
  id,
  fetchPermissions,
}: UpdatePolicyHandlerProps) => {
  try {
    setLoadingUpdatePolicy(true);

    const values = getValues();

    const objToSend: UpdateRequestPolicyProps = {
      idPoliticaSolicitud: id,
      clase: values?.clase,
      nombre: values?.nombre,
      activo: values?.activo,
      tipoSolicitud: values?.tipoSolicitud as number[],
      valor: getValorByClase(values),
    };

    await updateRequestPolicy(objToSend);

    toaster.create({
      type: "success",
      title: "Política actualizada con éxito.",
    });

    navigate(AppRoutes.REQUEST_POLICIES);

    setLoadingUpdatePolicy(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string; errors?: string }>;

    const errorMsj = errorObj?.response?.data?.errors;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.REQUEST_POLICIES,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorMsj ??
          errorObj?.response?.data?.message ??
          "No se pudo actualizar la política.",
      });
    }
    throw error;
  } finally {
    setLoadingUpdatePolicy(false);
  }
};

export interface HandleDeletePoliciesHandlerProps {
  setLoadingDeletePolicies: Dispatch<SetStateAction<boolean>>;
  setOpenConfirm: (value: React.SetStateAction<boolean>) => void;
  selection: string[];
  fetchPolicies: (showLoad?: boolean) => Promise<void>;
  setSelection: Dispatch<React.SetStateAction<string[]>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const handleDeletePoliciesHandler = async ({
  setLoadingDeletePolicies,
  setOpenConfirm,
  fetchPolicies,
  setSelection,
  selection,
  navigate,
  fetchPermissions,
}: HandleDeletePoliciesHandlerProps) => {
  try {
    setLoadingDeletePolicies(true);

    const objToSend: DeleteRequestPoliciesProps = {
      ids: selection.map(Number),
    };

    await deleteRequestPolicies(objToSend);

    toaster.create({
      type: "success",
      title: "Políticas seleccionadas eliminadas con éxito.",
    });

    setOpenConfirm(false);
    setLoadingDeletePolicies(false);

    setSelection([]);

    await fetchPolicies(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.REQUEST_POLICIES,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudieron eliminar las políticas seleccionadas.",
      });
    }
    throw error;
  } finally {
    setLoadingDeletePolicies(false);
    setOpenConfirm(false);
  }
};

interface FetchRequestPoliciesHandlerProps {
  setLoadingPolicies: Dispatch<SetStateAction<boolean>>;
  setPolicies: Dispatch<SetStateAction<RequestPolicy[] | null>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  showLoad?: boolean;
}

export const fetchRequestPoliciesHandler = async ({
  setLoadingPolicies,
  setPolicies,
  navigate,
  fetchPermissions,
  showLoad = true,
}: FetchRequestPoliciesHandlerProps) => {
  try {
    if (showLoad) setLoadingPolicies(true);

    const response = await getRequestPolicies();

    setPolicies(response);

    setLoadingPolicies(false);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo cargar las políticas de negocio.",
      });
    }
  } finally {
    setLoadingPolicies(false);
  }
};
